import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import AccountListSection from '~/components/app/AccountListSection';
import AccountNavButton from '~/components/app/AccountNavButton';
import InlineWebsitesSection from '~/components/app/InlineWebsitesSection';
import PlaceholderLink from '~/components/app/PlaceholderLink';
import CommunityLink from '~/components/app/CommunityLink';
import ExpandableNavSection from '~/components/patterns/navigations/ExpandableNavSection';
import TechnicalPhase2CompactLayout from '~/components/patterns/navigations/TechnicalPhase2CompactLayout';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';

import useNavigateToHomeDestination from '~/hooks/useNavigateToHomeDestination';

import {
	type messages
} from '../MainNavigation';



type UseNewPrimaryItemsParams = {
	areSecondaryNavItemsHidden: boolean,
	areTechnicalSubItemsHidden: boolean,
	collapseAllExpandables: () => void,
	handleAutoExpandNavigation: () => void,
	handleSearchIconClick: () => void,
	handleWebsitesIconClick: () => void,
	hasMultipleAccounts: boolean,
	isContentExpanded: boolean,
	isCurrentlyPhase2: boolean,
	isPerformanceExpanded: boolean,
	isTechnicalExpanded: boolean,
	messages: typeof messages,
	selectedAccountId: CK.AccountId | null,
	setAreSecondaryNavItemsHidden: (hidden: boolean) => void,
	setAreTechnicalSubItemsHidden: (hidden: boolean) => void,
	setContentExpanded: (expanded: boolean) => void,
	setPerformanceExpanded: (expanded: boolean) => void,
	setSelectedAccountId: (accountId: CK.AccountId | null) => void,
	setTechnicalExpanded: (expanded: boolean) => void,
	showPhase2CompactLayout: boolean,
	technicalSectionItems: Array<React.ReactElement>,
};

/**
 * Hook to build the primary navigation items for the new navigation layout.
 *
 * Returns an array of React elements representing the primary navigation items,
 * including Home, Content, Performance, Technical sections, and secondary items
 * (Workspace, Notifications, Community, Help).
 */
function useNewPrimaryItems(params: UseNewPrimaryItemsParams): Array<React.ReactElement> {
	const navigateToHomeDestination = useNavigateToHomeDestination();

	const {
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		collapseAllExpandables,
		handleAutoExpandNavigation,
		handleSearchIconClick,
		handleWebsitesIconClick,
		hasMultipleAccounts,
		isContentExpanded,
		isCurrentlyPhase2,
		isPerformanceExpanded,
		isTechnicalExpanded,
		messages,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setContentExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		technicalSectionItems,
	} = params;

	return React.useMemo(() => [
		// Home
		<li
			className="main-nav__item"
			key="home-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="home"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Home}
								/>
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.home} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Divider after Home
		<li
			className="main-nav__divider"
			key="home-divider"
		/>,
		// Content (expandable section)
		<ExpandableNavSection
			icon={(
				<NavigationIcon
					size={20}
					type={NavigationIconType.Content}
				/>
			)}
			isExpanded={isContentExpanded}
			isSubItemsHidden={false}
			key="content-section"
			label={(
				<FormattedMessage {...messages.content} />
			)}
			onToggle={() => {
				// If we're in Phase 2 and new navigation is active, expand navigation to Phase 3
				handleAutoExpandNavigation();
				const willExpand = !isContentExpanded;
				setContentExpanded(willExpand);
				if (willExpand) {
					setPerformanceExpanded(false);
					setTechnicalExpanded(false);
				}
			}}
		>
			{/* Writing Assistant */}
			<li
				className="main-nav__item"
				key="content-writing-assistant-item"
			>
				<PlaceholderLink sectionName="writingAssistant">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.writingAssistant} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
			{/* More */}
			<li
				className="main-nav__item"
				key="content-more-item"
			>
				<PlaceholderLink sectionName="more">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.more} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
		</ExpandableNavSection>,
		// Performance (expandable section)
		<ExpandableNavSection
			icon={(
				<NavigationIcon
					size={20}
					type={NavigationIconType.Performance}
				/>
			)}
			isExpanded={isPerformanceExpanded}
			isSubItemsHidden={false}
			key="performance-section"
			label={(
				<FormattedMessage {...messages.performance} />
			)}
			onToggle={() => {
				// If we're in Phase 2 and new navigation is active, expand navigation to Phase 3
				handleAutoExpandNavigation();
				const willExpand = !isPerformanceExpanded;
				setPerformanceExpanded(willExpand);
				if (willExpand) {
					setContentExpanded(false);
					setTechnicalExpanded(false);
				}
			}}
		>
			{/* Research */}
			<li
				className="main-nav__item"
				key="performance-research-item"
			>
				<PlaceholderLink sectionName="research">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.research} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
			{/* Websites */}
			<li
				className="main-nav__item"
				key="performance-websites-item"
			>
				<PlaceholderLink sectionName="websites">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.websites} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
			{/* AI Search */}
			<li
				className="main-nav__item"
				key="performance-ai-search-item"
			>
				<PlaceholderLink sectionName="aiSearch">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.aiSearch} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
			{/* Traditional Search */}
			<li
				className="main-nav__item"
				key="performance-traditional-search-item"
			>
				<PlaceholderLink sectionName="traditionalSearch">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.traditionalSearch} />
								)}
							/>
						);
					}}
				</PlaceholderLink>
			</li>
		</ExpandableNavSection>,
		// Technical (expandable section)
		<ExpandableNavSection
			accountListSection={hasMultipleAccounts && areSecondaryNavItemsHidden && !isCurrentlyPhase2 ? (
				<ul className="main-nav__account-list-section">
					<AccountListSection
						onAccountSelect={setSelectedAccountId}
						onAccountSelectComplete={() => {
							// Reset hidden states when account is selected from All Accounts Section
							setAreTechnicalSubItemsHidden(false);
							setAreSecondaryNavItemsHidden(false);
						}}
						selectedAccountId={selectedAccountId}
					/>
				</ul>
			) : undefined}
			accountNavButton={(
				<a
					className="main-nav__link"
					data-account-nav-button="true"
					href="#"
					onClick={(e) => {
						e.preventDefault();
						// If we're in Phase 2 and new navigation is active, expand navigation to Phase 3
						handleAutoExpandNavigation();
						// Hide Technical Sub-Items Container and Secondary Nav Items when AccountNavButton is clicked
						// Only hide if user has multiple accounts (same logic as old navigation)
						if (hasMultipleAccounts) {
							setAreTechnicalSubItemsHidden(true);
							setAreSecondaryNavItemsHidden(true);
						}
					}}
				>
					<AccountNavButton selectedAccountId={selectedAccountId} />
				</a>
			)}
			icon={(
				<NavigationIcon
					size={20}
					type={NavigationIconType.Technical}
				/>
			)}
			isExpanded={isTechnicalExpanded}
			isSubItemsHidden={areTechnicalSubItemsHidden}
			key="technical-section"
			label={(
				<FormattedMessage {...messages.technical} />
			)}
			onToggle={() => {
				// If we're in Phase 2 and new navigation is active, expand navigation to Phase 3
				handleAutoExpandNavigation();
				const willExpand = !isTechnicalExpanded;
				if (willExpand) {
					setTechnicalExpanded(true);
					setContentExpanded(false);
					setPerformanceExpanded(false);
					// Replicate post-login redirect: navigate to websites/dashboard/empty_state
					navigateToHomeDestination();
				} else if (areTechnicalSubItemsHidden && areSecondaryNavItemsHidden) {
					// Accounts list is visible - close it and return to sub-items instead of collapsing
					setAreTechnicalSubItemsHidden(false);
					setAreSecondaryNavItemsHidden(false);
				} else {
					setTechnicalExpanded(false);
				}
			}}
			phase2CompactLayout={showPhase2CompactLayout ? (
				<TechnicalPhase2CompactLayout
					onSearchClick={handleSearchIconClick}
					onWebsitesClick={handleWebsitesIconClick}
				/>
			) : undefined}
		>
			{/* Top divider - encloses websites dropdown section */}
			<li
				className="main-nav__divider"
				data-websites-section-divider="true"
				key="websites-section-divider-top"
			/>
			{/* Website Selector - appears first */}
			{technicalSectionItems[0]}
			{/* Inline websites section (search + list + add button) - appears after websites dropdown */}
			{selectedAccountId && isTechnicalExpanded && (
				<InlineWebsitesSection selectedAccountId={selectedAccountId} />
			)}
			{/* Bottom divider - encloses websites dropdown section */}
			<li
				className="main-nav__divider"
				data-websites-section-divider="true"
				key="websites-section-divider-bottom"
			/>
			{/* Dashboard, Pages, Platform, Issues, Alerts - appears after websites section */}
			{technicalSectionItems.slice(1)}
		</ExpandableNavSection>,
		// Divider after Content/Performance/Technical section
		<li
			className="main-nav__divider"
			key="content-performance-technical-divider"
		/>,
		// Workspace (new dead link)
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="workspace-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="workspace"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Workspace}
								/>
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.workspace} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Divider after Workspace
		<li
			className="main-nav__divider"
			data-secondary-nav-divider="true"
			key="workspace-divider"
		/>,
		// Notifications (new dead link)
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="notifications-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="notifications"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Notifications}
								/>
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.notifications} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Community
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="community-item"
		>
			<CommunityLink onClick={collapseAllExpandables}>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Community}
							/>
						)}
						isActive={isActive}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.community} />
						)}
					/>
				)}
			</CommunityLink>
		</li>,
		// Help
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="help-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="help"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Help}
								/>
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.help} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Divider after Notifications/Community/Help section
		<li
			className="main-nav__divider"
			data-secondary-nav-divider="true"
			key="notifications-community-help-divider"
		/>,
	], [
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		collapseAllExpandables,
		navigateToHomeDestination,
		handleAutoExpandNavigation,
		handleSearchIconClick,
		handleWebsitesIconClick,
		hasMultipleAccounts,
		isContentExpanded,
		isCurrentlyPhase2,
		isPerformanceExpanded,
		isTechnicalExpanded,
		messages,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setContentExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		technicalSectionItems,
	]);
}

export default useNewPrimaryItems;
